import { computeRealizedR, type ClosedTradeForMetrics } from './tradeCalculations'

export const LOW_SAMPLE_THRESHOLD = 30
export const PLAN_R_BUCKET = -1
export const R_BUCKETS = [-3, -2, -1.5, -1, -0.5, 0, 0.5, 1, 2] as const

export type RBucket = (typeof R_BUCKETS)[number]
export type StopComplianceKind = 'atStop' | 'early' | 'widened'
export type HoldBucketId = 'under1' | 'd1to3' | 'd3to7' | 'over7'
export type ErrorTagId = 'widenedStop' | 'fomo' | 'earlyExit' | 'other' | 'untagged'

export interface ProcessTrade {
  id: string
  symbol: string
  long_short?: string | null
  entry_date?: string | null
  exit_date?: string | null
  entry_price?: number | null
  exit_price?: number | null
  stop_loss?: number | null
  rr?: number | null
  pnl?: number | null
  convertedPnl?: number | null
  fee?: number | null
  convertedFee?: number | null
  tags?: string[] | null
  total_entry_amount?: number | null
  position_size?: number | null
}

export interface StopDeviationRow {
  id: string
  symbol: string
  plannedPct: number
  realizedPct: number
  absDelta: number
}

export interface StopComplianceSegment {
  kind: StopComplianceKind
  count: number
  share: number
}

export interface RHistogramBar {
  bucket: RBucket
  label: string
  count: number
  isPlan: boolean
  tone: 'loss' | 'neutral' | 'gain'
}

export interface CostRow {
  id: 'gross' | 'commission' | 'net'
  value: number
}

export interface HoldDuration {
  days: number
  hours: number
  totalHours: number
}

export interface HoldBucket {
  id: HoldBucketId
  pnl: number
  count: number
}

export interface ErrorTagRow {
  id: ErrorTagId
  pnl: number
  count: number
  avgPnl: number
}

export interface ProcessAnalytics {
  sampleCount: number
  lowSample: boolean
  stop: {
    sampleCount: number
    plannedAvgPct: number | null
    realizedAvgPct: number | null
    deviationPct: number | null
    largestDeviations: StopDeviationRow[]
    compliance: StopComplianceSegment[]
    excessLossShare: number | null
  }
  rDistribution: {
    sampleCount: number
    bars: RHistogramBar[]
    plannedRr: number | null
    realizedRr: number | null
    losersBeyondPlan: number
    losingCount: number
    avgRealizedR: number | null
  }
  cost: {
    sampleCount: number
    grossPnl: number
    commission: number
    netPnl: number
    rows: CostRow[]
    totalFees: number
    avgFee: number | null
    feeShareOfNet: number | null
    breakEvenMovePct: number | null
  }
  hold: {
    sampleCount: number
    winners: HoldDuration | null
    losers: HoldDuration | null
    durationRatio: number | null
    buckets: HoldBucket[]
    longHoldLossShare: number | null
  }
  tags: {
    sampleCount: number
    rows: ErrorTagRow[]
    topLossShare: number | null
    untaggedCount: number
  }
}

const STOP_AT_TOLERANCE = 0.15
const TAG_ALIASES: Record<Exclude<ErrorTagId, 'other' | 'untagged'>, string[]> = {
  widenedStop: [
    'widened-stop',
    'widened_stop',
    'widen-stop',
    'stop-widened',
    'stop-extended',
    'widened',
    'genislet',
    'genişlet',
    'stop genislet',
    'stop genişlet',
    'stopu genisletti',
    'stopu genişletti',
    "stop'u genişletti",
  ],
  fomo: ['fomo', 'fomo-entry', 'fomo_entry', 'fomo-giris', 'fomo giriş', 'fomo-giriş'],
  earlyExit: ['early-exit', 'early_exit', 'early exit', 'early', 'erken-cikis', 'erken çıkış', 'erken-çıkış'],
}

function avg(values: number[]): number | null {
  if (!values.length) return null
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

function tradePnl(trade: ProcessTrade): number {
  return trade.convertedPnl ?? trade.pnl ?? 0
}

function tradeFee(trade: ProcessTrade): number {
  const fee = trade.convertedFee ?? trade.fee ?? 0
  return Number.isFinite(fee) ? Math.abs(fee) : 0
}

function asClosedTrade(trade: ProcessTrade): ClosedTradeForMetrics {
  return {
    exit_date: trade.exit_date ?? null,
    entry_date: trade.entry_date ?? null,
    pnl: trade.convertedPnl ?? trade.pnl ?? null,
    pnl_percentage: null,
    strategy_id: null,
    entry_price: trade.entry_price,
    exit_price: trade.exit_price,
    stop_loss: trade.stop_loss,
    long_short: trade.long_short,
  }
}

function sideOf(trade: ProcessTrade): 'long' | 'short' {
  return (trade.long_short || '').toLowerCase() === 'short' ? 'short' : 'long'
}

function plannedStopPct(trade: ProcessTrade): number | null {
  const entry = trade.entry_price
  const sl = trade.stop_loss
  if (entry == null || sl == null || entry === 0) return null
  const pct = (Math.abs(entry - sl) / Math.abs(entry)) * 100
  return Number.isFinite(pct) ? pct : null
}

function realizedStopPct(trade: ProcessTrade): number | null {
  const entry = trade.entry_price
  const exit = trade.exit_price
  if (entry == null || exit == null || entry === 0) return null
  const pct = (Math.abs(entry - exit) / Math.abs(entry)) * 100
  return Number.isFinite(pct) ? pct : null
}

function stopRatio(trade: ProcessTrade): number | null {
  const entry = trade.entry_price
  const exit = trade.exit_price
  const sl = trade.stop_loss
  if (entry == null || exit == null || sl == null) return null
  const plannedRisk = sideOf(trade) === 'long' ? entry - sl : sl - entry
  if (plannedRisk <= 0) return null
  const moveTowardStop = sideOf(trade) === 'long' ? entry - exit : exit - entry
  return moveTowardStop / plannedRisk
}

export function classifyStopCompliance(trade: ProcessTrade): StopComplianceKind | null {
  const ratio = stopRatio(trade)
  if (ratio == null) return null
  if (ratio > 1 + STOP_AT_TOLERANCE) return 'widened'
  if (ratio >= 1 - STOP_AT_TOLERANCE) return 'atStop'
  return 'early'
}

export function assignRBucket(value: number): RBucket {
  if (value < -2.5) return -3
  if (value < -1.75) return -2
  if (value < -1.25) return -1.5
  if (value < -0.75) return -1
  if (value < -0.25) return -0.5
  if (value < 0.25) return 0
  if (value < 0.75) return 0.5
  if (value < 1.5) return 1
  return 2
}

export function formatRBucketLabel(bucket: RBucket): string {
  if (bucket === 0) return '0'
  const sign = bucket > 0 ? '+' : ''
  return `${sign}${bucket}R`
}

function holdHours(trade: ProcessTrade): number | null {
  if (!trade.entry_date || !trade.exit_date) return null
  const start = new Date(trade.entry_date).getTime()
  const end = new Date(trade.exit_date).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null
  return (end - start) / 36e5
}

export function hoursToDuration(totalHours: number): HoldDuration {
  const safe = Math.max(0, totalHours)
  const days = Math.floor(safe / 24)
  const hours = Math.floor(safe - days * 24)
  return { days, hours, totalHours: safe }
}

function holdBucketId(totalHours: number): HoldBucketId {
  if (totalHours < 24) return 'under1'
  if (totalHours < 72) return 'd1to3'
  if (totalHours < 168) return 'd3to7'
  return 'over7'
}

function normalizeTag(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase('tr')
    .replace(/[_]+/g, '-')
    .replace(/\s+/g, ' ')
}

export function classifyErrorTag(raw: string): Exclude<ErrorTagId, 'untagged'> {
  const tag = normalizeTag(raw)
  const compact = tag.replace(/\s+/g, '-')
  for (const [id, aliases] of Object.entries(TAG_ALIASES) as Array<[Exclude<ErrorTagId, 'other' | 'untagged'>, string[]]>) {
    if (aliases.some((alias) => tag === alias || compact === alias || tag.includes(alias))) {
      return id
    }
  }
  return 'other'
}

function share(part: number, total: number): number | null {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return null
  return part / total
}

export function buildProcessAnalytics(trades: ProcessTrade[]): ProcessAnalytics {
  const closed = trades.filter((trade) => Boolean(trade.exit_date))
  const sampleCount = closed.length

  const stopSamples = closed
    .map((trade) => {
      const plannedPct = plannedStopPct(trade)
      const realizedPct = realizedStopPct(trade)
      if (plannedPct == null || realizedPct == null) return null
      return { trade, plannedPct, realizedPct, absDelta: Math.abs(realizedPct - plannedPct) }
    })
    .filter((row): row is NonNullable<typeof row> => row != null)

  const plannedAvgPct = avg(stopSamples.map((row) => row.plannedPct))
  const realizedAvgPct = avg(stopSamples.map((row) => row.realizedPct))
  const deviationPct =
    plannedAvgPct != null && plannedAvgPct > 0 && realizedAvgPct != null
      ? ((realizedAvgPct - plannedAvgPct) / plannedAvgPct) * 100
      : null

  const largestDeviations = [...stopSamples]
    .sort((a, b) => b.absDelta - a.absDelta)
    .reduce<StopDeviationRow[]>((rows, row) => {
      if (rows.some((existing) => existing.symbol === row.trade.symbol)) return rows
      rows.push({
        id: row.trade.id,
        symbol: row.trade.symbol,
        plannedPct: row.plannedPct,
        realizedPct: row.realizedPct,
        absDelta: row.absDelta,
      })
      return rows
    }, [])
    .slice(0, 6)

  const complianceCounts: Record<StopComplianceKind, number> = { atStop: 0, early: 0, widened: 0 }
  let classified = 0
  for (const trade of closed) {
    const kind = classifyStopCompliance(trade)
    if (!kind) continue
    complianceCounts[kind] += 1
    classified += 1
  }
  const compliance: StopComplianceSegment[] = (['atStop', 'early', 'widened'] as const).map((kind) => ({
    kind,
    count: complianceCounts[kind],
    share: classified > 0 ? complianceCounts[kind] / classified : 0,
  }))

  const totalLoss = Math.abs(
    closed.filter((trade) => tradePnl(trade) < 0).reduce((sum, trade) => sum + tradePnl(trade), 0)
  )
  const excessLoss = stopSamples.reduce((sum, row) => {
    const pnl = tradePnl(row.trade)
    if (pnl >= 0 || row.realizedPct <= 0 || row.realizedPct <= row.plannedPct) return sum
    return sum + Math.abs(pnl) * ((row.realizedPct - row.plannedPct) / row.realizedPct)
  }, 0)

  const realizedValues = closed
    .map((trade) => computeRealizedR(asClosedTrade(trade)))
    .filter((value): value is number => value != null)
  const plannedRr = avg(closed.map((trade) => trade.rr).filter((value): value is number => value != null && value > 0))
  const winningR = closed
    .filter((trade) => tradePnl(trade) > 0)
    .map((trade) => computeRealizedR(asClosedTrade(trade)))
    .filter((value): value is number => value != null)
  const losingR = closed
    .filter((trade) => tradePnl(trade) < 0)
    .map((trade) => computeRealizedR(asClosedTrade(trade)))
    .filter((value): value is number => value != null)
  const bucketCounts = Object.fromEntries(R_BUCKETS.map((bucket) => [bucket, 0])) as Record<RBucket, number>
  for (const value of realizedValues) bucketCounts[assignRBucket(value)] += 1

  const netPnl = closed.reduce((sum, trade) => sum + tradePnl(trade), 0)
  const totalFees = closed.reduce((sum, trade) => sum + tradeFee(trade), 0)
  const grossPnl = netPnl + totalFees
  const invested = closed.reduce((sum, trade) => sum + (trade.total_entry_amount ?? trade.position_size ?? 0), 0)

  const winnerHours = closed.filter((trade) => tradePnl(trade) > 0).map(holdHours).filter((value): value is number => value != null)
  const loserHours = closed.filter((trade) => tradePnl(trade) < 0).map(holdHours).filter((value): value is number => value != null)
  const holdBuckets: HoldBucket[] = [
    { id: 'under1', pnl: 0, count: 0 },
    { id: 'd1to3', pnl: 0, count: 0 },
    { id: 'd3to7', pnl: 0, count: 0 },
    { id: 'over7', pnl: 0, count: 0 },
  ]
  let holdSample = 0
  let longHoldLoss = 0
  for (const trade of closed) {
    const hours = holdHours(trade)
    if (hours == null) continue
    holdSample += 1
    const bucket = holdBuckets.find((item) => item.id === holdBucketId(hours))
    if (!bucket) continue
    const pnl = tradePnl(trade)
    bucket.pnl += pnl
    bucket.count += 1
    if (bucket.id === 'over7' && pnl < 0) longHoldLoss += Math.abs(pnl)
  }

  const tagTotals: Record<ErrorTagId, { pnl: number; count: number }> = {
    widenedStop: { pnl: 0, count: 0 },
    fomo: { pnl: 0, count: 0 },
    earlyExit: { pnl: 0, count: 0 },
    other: { pnl: 0, count: 0 },
    untagged: { pnl: 0, count: 0 },
  }
  for (const trade of closed) {
    const tags = (trade.tags || []).map((tag) => String(tag).trim()).filter(Boolean)
    const pnl = tradePnl(trade)
    if (!tags.length) {
      tagTotals.untagged.pnl += pnl
      tagTotals.untagged.count += 1
      continue
    }
    const unique = new Set(tags.map(classifyErrorTag))
    for (const id of unique) {
      tagTotals[id].pnl += pnl
      tagTotals[id].count += 1
    }
  }
  const tagRows: ErrorTagRow[] = (['widenedStop', 'fomo', 'earlyExit', 'other', 'untagged'] as const)
    .filter((id) => tagTotals[id].count > 0)
    .map((id) => ({
      id,
      pnl: tagTotals[id].pnl,
      count: tagTotals[id].count,
      avgPnl: tagTotals[id].count ? tagTotals[id].pnl / tagTotals[id].count : 0,
    }))
  const taggedLoss = tagRows
    .filter((row) => row.id !== 'untagged' && row.pnl < 0)
    .reduce((sum, row) => sum + Math.abs(row.pnl), 0)
  const topTaggedLoss = Math.max(0, ...tagRows.filter((row) => row.id !== 'untagged' && row.pnl < 0).map((row) => Math.abs(row.pnl)))

  return {
    sampleCount,
    lowSample: sampleCount > 0 && sampleCount < LOW_SAMPLE_THRESHOLD,
    stop: {
      sampleCount: stopSamples.length,
      plannedAvgPct,
      realizedAvgPct,
      deviationPct,
      largestDeviations,
      compliance,
      excessLossShare: share(excessLoss, totalLoss),
    },
    rDistribution: {
      sampleCount: realizedValues.length,
      bars: R_BUCKETS.map((bucket) => ({
        bucket,
        label: formatRBucketLabel(bucket),
        count: bucketCounts[bucket],
        isPlan: bucket === PLAN_R_BUCKET,
        tone: bucket < 0 ? 'loss' : bucket > 0 ? 'gain' : 'neutral',
      })),
      plannedRr,
      realizedRr: avg(winningR),
      losersBeyondPlan: losingR.filter((value) => value < PLAN_R_BUCKET).length,
      losingCount: losingR.length,
      avgRealizedR: avg(realizedValues),
    },
    cost: {
      sampleCount,
      grossPnl,
      commission: -totalFees,
      netPnl,
      rows: [
        { id: 'gross', value: grossPnl },
        { id: 'commission', value: -totalFees },
        { id: 'net', value: netPnl },
      ],
      totalFees,
      avgFee: sampleCount > 0 ? totalFees / sampleCount : null,
      feeShareOfNet: share(totalFees, Math.abs(netPnl)),
      breakEvenMovePct: invested > 0 ? (totalFees / invested) * 100 : null,
    },
    hold: {
      sampleCount: holdSample,
      winners: winnerHours.length ? hoursToDuration(avg(winnerHours) ?? 0) : null,
      losers: loserHours.length ? hoursToDuration(avg(loserHours) ?? 0) : null,
      durationRatio:
        winnerHours.length && loserHours.length && (avg(winnerHours) ?? 0) > 0
          ? (avg(loserHours) ?? 0) / (avg(winnerHours) ?? 1)
          : null,
      buckets: holdBuckets,
      longHoldLossShare: share(longHoldLoss, totalLoss),
    },
    tags: {
      sampleCount,
      rows: tagRows,
      topLossShare: share(topTaggedLoss, taggedLoss || totalLoss),
      untaggedCount: tagTotals.untagged.count,
    },
  }
}
